/**
 * PDF Display Utilities
 *
 * Utilities for opening PDF documents generated with @react-pdf/renderer.
 *
 * SHER-197: PDFs now open inline in a new tab (browser's native PDF viewer)
 * instead of triggering a download. The native viewer already exposes a
 * Download button so users can still save when they want to. This avoids
 * accumulating clinical PDFs in the local Downloads folder on shared
 * hospital machines.
 */

import { pdf } from '@react-pdf/renderer';
import { ReactElement } from 'react';

/**
 * Open a PDF document inline in a new browser tab.
 *
 * Name kept as `downloadPDF` for backwards compatibility with existing
 * callers (PDFDownloadButton, BillView, insuranceBillUtils, Billing/index).
 * Despite the historical name, this no longer forces a download — see SHER-197.
 *
 * @param pdfDocument - React element created with @react-pdf/renderer components
 * @param filename - Document title (shown in the new tab)
 */
export const downloadPDF = async (pdfDocument: ReactElement, filename: string): Promise<void> => {
  try {
    const blob = await pdf(pdfDocument).toBlob();
    const url = URL.createObjectURL(blob);

    const newWindow = window.open(url, '_blank');
    if (newWindow && filename) {
      newWindow.document.title = filename;
    }

    // Revoke after a delay so the new tab has time to fetch the blob.
    // Without this, repeated PDF generations leak ObjectURLs.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    console.error('Error opening PDF:', error);
    throw new Error('Failed to open PDF');
  }
};

/**
 * Generate PDF blob for custom handling
 * @param pdfDocument - React element created with @react-pdf/renderer components
 * @returns Promise<Blob> - PDF blob
 */
export const generatePDFBlob = async (pdfDocument: ReactElement): Promise<Blob> => {
  try {
    return await pdf(pdfDocument).toBlob();
  } catch (error) {
    console.error('Error generating PDF blob:', error);
    throw new Error('Failed to generate PDF');
  }
};

/**
 * PDF generation status enum
 */
export enum PDFGenerationStatus {
  IDLE = 'idle',
  GENERATING = 'generating',
  SUCCESS = 'success',
  ERROR = 'error'
}

/**
 * Hook-like interface for PDF generation state management
 * (This is a simple state object, not a React hook)
 */
export interface PDFGenerationState {
  status: PDFGenerationStatus;
  error?: string;
}

/**
 * Create initial PDF generation state
 */
export const createPDFGenerationState = (): PDFGenerationState => ({
  status: PDFGenerationStatus.IDLE
});

/**
 * Utility to get user-friendly status messages
 */
export const getPDFStatusMessage = (status: PDFGenerationStatus, locale: string = 'fr'): string => {
  const messages = {
    fr: {
      [PDFGenerationStatus.IDLE]: 'Prêt',
      [PDFGenerationStatus.GENERATING]: 'Génération du PDF...',
      [PDFGenerationStatus.SUCCESS]: 'PDF généré avec succès',
      [PDFGenerationStatus.ERROR]: 'Erreur lors de la génération du PDF'
    },
    en: {
      [PDFGenerationStatus.IDLE]: 'Ready',
      [PDFGenerationStatus.GENERATING]: 'Generating PDF...',
      [PDFGenerationStatus.SUCCESS]: 'PDF generated successfully',
      [PDFGenerationStatus.ERROR]: 'Error generating PDF'
    }
  };

  return messages[locale as keyof typeof messages]?.[status] || messages.en[status];
};
